<template>
  <div
    v-reached.center="onShowBanner"
    :class="classes"
  >
    <div class="banner__inner">
      <client-only>
        <a
          class="banner__link"
          :href="getBannerData(data, ['body', 'url'])"
          :target="getBannerData(data, ['body', 'target'])"
          @click="onClick()"
        >
          <media
            v-if="backAsImg"
            class="banner__background-img"
            :src="getBannerData(data, ['body', 'image'])"
            :alt="getBannerData(data, ['body', 'alt'])"
            @on-load="$emit('banner-loaded')"
          />
          <div
            v-else
            class="banner__background"
            :style="getBgImageUrl('image')"
          />
          <div
            v-if="getBannerData(data, ['body', 'svg'])"
            class="banner__top"
          >
            <div
              class="banner__foreground"
              :style="getBgImageUrl('svg')"
            />
          </div>
        </a>
      </client-only>
    </div>
    <client-only>
      <div
        v-if="getBannerData(data, ['links', 'length'])"
        :class="linksClasses(data)"
      >
        <a
          v-for="link in getBannerData(data, ['links'])"
          :key="link.text"
          :href="link.url"
          :style="fontStyles(link.font)"
          class="banner__links-item"
          @click="onClick(link)"
        >
          {{ link.text }}
        </a>
      </div>
    </client-only>
  </div>
</template>

<script lang="ts">
/* eslint-disable no-param-reassign */
import objectPath from 'object-path';
import { throttle } from 'throttle-debounce';
import { defineComponent } from 'vue';

import { bannersGtmEvents } from '@/gtm/banners-gtm-events';
import { formatImageUrl } from '@/utils/helpers/format-image-url';

export default defineComponent({
  name: 'banner',

  inject: {
    adaptive: 'adaptive',
  },

  props: {
    data: {
      type: Object,
      required: true,
    },

    backAsImg: {
      type: Boolean,
      default: true,
    },

    slotName: {
      type: String,
      default: '',
    },

    // индекс баннера в свайпере
    index: {
      type: Number,
      default: 0,
    },

    // находится ли баннер в свайпере
    inSlider: {
      type: Boolean,
      default: false,
    },
  },

  emits: [
    'banner-loaded',
  ],

  data() {
    return { sendEvents: [] };
  },

  computed: {
    classes() {
      const base = 'banner';

      return [base, { [`${base}_mode_slide`]: !this.backAsImg }];
    },
  },

  created() {
    if (!(typeof window === 'undefined')) {
      window.addEventListener('resize', this.onResize);
      this.onResize();
    }
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.onResize);
  },

  methods: {
    getBannerData(bannerData, path = []) {
      const key = this.adaptive.isMobile ? 'mobile' : 'desktop';

      return objectPath.get(bannerData.content?.[key], path);
    },
    onResize: throttle(100, function () {
      if (this.adaptive.isMobile) {
        this.slideButtonsPositionReset();
        this.slideButtonsPosition();
      } else {
        this.slideButtonsPositionReset();
      }
    }),
    slideButtonsPositionReset() {
      const linksWraps = document.querySelectorAll('.banner__links');

      linksWraps.forEach((linkWrap) => {
        linkWrap.removeAttribute('style');
      });
    },
    // для мобильной версии, кнопки подтягиваем на середину
    slideButtonsPosition() {
      const linksWraps = document.querySelectorAll('.banner__links');

      linksWraps.forEach((linkWrap) => {
        const buttonsCount = linkWrap.childElementCount;
        const rows = Math.ceil(buttonsCount / 2);

        if (rows >= 2) {
          // высота для отступов между кнопками
          linkWrap.style.minHeight = `${linkWrap.offsetHeight + (rows * 6)}px`;
        }

        const halfLinksWrap = linkWrap.offsetHeight / 2;

        linkWrap.style.marginTop = `-${halfLinksWrap}px`;
      });
    },
    linksClasses(bannerData) {
      return ['banner__links', this.getBannerData(bannerData, ['linksAlign']), {
        single: this.getBannerData(bannerData, ['links']).length === 1,
      }];
    },
    fontStyles({
      color, underscore, size, register, bold,
    }) {
      if (this.adaptive.isMobile) return;

      return {
        color,
        fontSize: `${size}px`,
        textTransform: register,
        fontWeight: bold ? 'bold' : 'normal',
        borderColor: underscore ? color : 'transparent',
      };
    },
    onClick(link) {
      if (!this.slotName) return;

      bannersGtmEvents.clickBanner({
        slotName: this.slotName,
        imageUrl: this.getBannerData(this.data, ['body', 'image']),
        hostUrl: this.$route?.fullPath,
        text: link?.text,
        redirectUrl: link?.url || this.getBannerData(this.data, ['body', 'url']),
        index: this.inSlider ? this.index : null,
      });

      this.$persEvents.sendBannersClickEvent({
        id: this.data?.id,
        slotId: this.slotName,
      });
    },
    // TODO: Покрыть в тестах из-за бага https://jira.app.local/browse/ESMFUN-2171
    onShowBanner() {
      if (this.getBannerData(this.data) !== undefined) {
        bannersGtmEvents.showBanner({
          slotName: this.slotName,
          imageUrl: this.getBannerData(this.data, ['body', 'image']),
          hostUrl: this.$route?.fullPath,
          redirectUrl: this.getBannerData(this.data, ['body', 'url']),
          index: this.inSlider ? this.index : null,
        });
      }

      if (this.data?.id && !this.sendEvents.includes(this.data?.id)) {
        this.sendEvents.push(this.data?.id);
        this.$persEvents.addBannersAppearEvent(this.data?.id, this.slotName);
        this.$persEvents.debouncedEventBanners();
      }
    },
    getBgImageUrl(key: string): string {
      return `background-image: ${formatImageUrl(this.getBannerData(this.data, ['body', key]))}`;
    },
  },
});
</script>

<style lang="scss">
  .banner {
    position: relative;

    &_mode_slide {
      max-width: 1920px;
      margin: 0 auto;

      .banner__inner {
        height: 272px;

        @include breakpoint("tablet") {
          height: 306px;
        }

        @include breakpoint("desktop") {
          height: 520px;
        }
      }
    }

    &:not(.banner_mode_slide) {
      .banner__links {
        @include breakpoint("tablet") {
          left: 16px;
          right: 16px;
        }

        @include breakpoint("desktop") {
          left: 24px;
          right: 24px;
        }
      }
    }

    &__link {
      display: block;
      height: 100%;
      font-size: 0;
      text-align: center;
    }

    &__background-img {
      max-width: 100%;
      height: auto;
    }

    &__background,
    &__foreground {
      height: 100%;
      background-size: cover;
      background-repeat: no-repeat;
      background-position: top center;
    }

    &__foreground {
      max-width: 1224px;
      margin: auto;
    }

    &__top {
      position: absolute;
      top: 0;
      bottom: 0;
      margin: auto;
      width: 100%;
    }

    &__links {
      background: $white;
      padding: 24px 16px;
      border: 1px solid #f0f0f0;
      box-shadow: 0 4px 24px rgba(0, 0, 0, .08);
      margin-left: 16px;
      margin-right: 16px;
      display: flex;
      flex-wrap: wrap;
      align-content: space-between;
      position: relative;
      justify-content: space-between;

      &.single {
        justify-content: center;
      }

      @include breakpoint("tablet") {
        background: none;
        padding: 0;
        height: auto;
        box-shadow: none;
        justify-content: unset;
        border: 0;
        bottom: 64px;
        max-width: 615px;
        margin: auto;
        position: absolute;
        left: 0;
        right: 0;

        &.left {
          justify-content: flex-start;
        }

        &.right {
          justify-content: flex-end;
        }

        &.center {
          justify-content: center;
        }
      }

      @include breakpoint("desktop") {
        max-width: 1036px;
        bottom: 110px;
      }
    }

    &__links-item {
      background: $blue;
      font-size: 14px;
      font-weight: 600;
      color: $white;
      border-radius: 4px;
      text-align: center;
      line-height: 32px;
      height: 32px;
      flex: 0 0 calc(50% - 6px);

      @include breakpoint("tablet") {
        font-size: 16px;
        display: block;
        color: $white;
        background: none;
        border: 0;
        border-bottom: 2px solid $white;
        flex: 0 0 auto;
        border-radius: 0;
        line-height: 20px;
        height: 30px;
        margin-right: 16px;

        &:last-child {
          margin-right: 0;
        }
      }

      @include breakpoint("desktop") {
        margin-right: 24px;
      }
    }
  }
</style>
